import React, { useState, useEffect } from 'react';
import { DateTimeSelector } from './DateTimeSelector'; // Adjust the path as needed

const ViewTest = ({ selectedCompany, selectedJob }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [deletingTests, setDeletingTests] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showReschedulePopup, setShowReschedulePopup] = useState(false);
  const [rescheduleTestId, setRescheduleTestId] = useState(null);
  const [rescheduleCandidateName, setRescheduleCandidateName] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [isRescheduling, setIsRescheduling] = useState(false);

  useEffect(() => {
    // Only fetch tests if we have a selected company and job
    if (!selectedCompany || !selectedJob) {
      setTests([]);
      setLoading(false);
      return;
    }

    // Fetch tests for the specific job
    fetch(`/api/get_job_tests/${selectedJob.job_id}`)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          // Transform data to create individual cards for each candidate
          const individualTests = [];
          data.forEach(test => {
            if (test.candidate_names && test.candidate_names.length > 0) {
              // Create individual test card for each candidate
              test.candidate_names.forEach((candidateName, index) => {
                individualTests.push({
                  ...test,
                  candidate_name: candidateName,
                  candidate_email: test.selected_users ? test.selected_users.split(',')[index]?.trim() : '',
                  individual_test_id: `${test.test_id}_${index}`,
                  is_completed: index < test.total_completed // Simple logic - can be improved
                });
              });
            } else if (test.selected_users && test.selected_users.trim() !== '') {
              // If there are selected_users but no candidate_names, it's a data inconsistency
              // Split the users and create cards for each
              const user_emails = test.selected_users.split(',').map(email => email.trim()).filter(email => email);
              user_emails.forEach((email, index) => {
                individualTests.push({
                  ...test,
                  candidate_name: `Candidate ${index + 1}`, // Fallback name
                  candidate_email: email,
                  individual_test_id: `${test.test_id}_${index}`,
                  is_completed: index < test.total_completed
                });
              });
            }
            // No else clause - if no candidates and no selected_users, don't show the test
          });
          setTests(individualTests);
        } else {
          console.warn("Invalid data format:", data);
          setTests([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching tests:", err);
        setLoading(false);
      });
  }, [selectedCompany, selectedJob]);

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

  // Function to get test type display name
  const getTestTypeDisplay = (testType) => {
    switch (testType) {
      case 'b':
        return 'Behavioral Test';
      case 'p':
        return 'Personality Test';
      case 't':
        return 'Screening Test';
      case 'c':
        return 'Coding Test';
      case 'a':
        return 'Personality & Behavioral Test';
      case 'o':
        return 'Communication Test';
      default:
        return 'Unknown Test Type';
    }
  };

  // Filter tests based on active tab
  const filteredTests = tests.filter(test => {
    if (activeTab === 'all') return true;
    return test.test_type === activeTab;
  });

  // Handle delete test button click
  const handleDeleteClick = (testId, candidateEmail, candidateName) => {
    // Debug log removed — was leaking testId/candidate email/name to console.
    setDeleteTarget({ testId, candidateEmail, candidateName });
    setShowDeleteConfirm(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    const { testId, candidateEmail, candidateName } = deleteTarget;
    // Debug log removed — was leaking delete-target PII to console.

    const deleteKey = `${testId}_${candidateEmail}`;
    setDeletingTests(prev => new Set(prev).add(deleteKey));
    setShowDeleteConfirm(false);
    setDeleteTarget(null);

    try {
      // Debug log removed — was state-machine noise.
      const requestBody = {
        test_id: testId,
        candidate_email: candidateEmail
      };
      // Debug log removed — was leaking request body (testId, candidate email) to console.

      const response = await fetch('/api/delete_test', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      // Debug log removed — was leaking response status to console.
      const data = await response.json();
      // Debug log removed — was leaking response payload to console.

      if (response.ok) {
        // Debug log removed — was state-machine noise.
        // Remove the deleted test from the state
        setTests(prevTests => {
          const filtered = prevTests.filter(test =>
            !(test.test_id === testId && test.candidate_email === candidateEmail)
          );
          // Debug log removed — was leaking tests count to console.
          return filtered;
        });
        alert('Test deleted successfully!');
      } else {
        console.error('API error:', data);
        alert(`Error: ${data.error || 'Failed to delete test'}`);
      }
    } catch (error) {
      console.error('Error deleting test:', error);
      alert('Error deleting test. Please try again.');
    } finally {
      setDeletingTests(prev => {
        const newSet = new Set(prev);
        newSet.delete(deleteKey);
        return newSet;
      });
    }
  };

  // Handle delete cancellation
  const handleDeleteCancel = () => {
    // Debug log removed — was state-machine noise.
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  };

  const handleRescheduleTest = (individualTestId, candidateName) => {
    setRescheduleTestId(individualTestId);
    setRescheduleCandidateName(candidateName);
    setRescheduleTime('');
    setRescheduleDate('');
    setShowReschedulePopup(true);
  };

  const handleRescheduleSubmit = async () => {
    if (!rescheduleTestId || !rescheduleDate || !rescheduleTime) {
      alert('Please select date and time');
      return;
    }
  
    setIsRescheduling(true);
    
    try {
      const datetimeString = `${rescheduleDate}T${rescheduleTime}:00`;
      const requestBody = {
        test_id: rescheduleTestId.split('_')[0], // Extract original test ID
        candidate_email: tests.find(t => t.individual_test_id === rescheduleTestId)?.candidate_email,
        new_scheduled_time: datetimeString
      };
  
      const response = await fetch('/api/reschedule_test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
  
      const data = await response.json();
  
      if (response.ok) {
        // Update the test in state with new time
        setTests(prevTests => 
          prevTests.map(test => 
            test.individual_test_id === rescheduleTestId
              ? { ...test, scheduled_time: datetimeString }
              : test
          )
        );
        setShowReschedulePopup(false);
        alert('Test rescheduled successfully!');
      } else {
        alert(`Error: ${data.error || 'Failed to reschedule test'}`);
      }
    } catch (error) {
      console.error('Error rescheduling test:', error);
      alert('Error rescheduling test. Please try again.');
    } finally {
      setIsRescheduling(false);
    }
  };

  // Show message if no company or job is selected
  if (!selectedCompany || !selectedJob) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 m-6">
        <h1 className="text-xl font-bold text-slate-800 mb-3">Test Management</h1>
        <p style={{ textAlign: 'center', marginTop: '20px', color: '#666' }}>
          Please select a company and job to view tests.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 m-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-slate-800">{selectedJob.job_title}</h2>
          <p className="text-sm text-slate-500">{selectedCompany.name} • {selectedJob.location} • {selectedJob.experience_range}</p>
        </div>
      </div>

      <div className="flex gap-2 mb-5 overflow-x-auto">
        {['all', 'b', 'p', 't', 'c'].map((tab) => {
          const labels = { all: 'All Tests', b: 'Behavior Test', p: 'Personality Test', t: 'Screening Test', c: 'Coding Test' };
          return (
            <span
              key={tab}
              className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${activeTab === tab ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}
              onClick={() => setActiveTab(tab)}
            >
              {labels[tab]}
            </span>
          );
        })}
      </div>

      <div className="mb-4 relative">
        <button className="px-4 py-2 border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors" onClick={toggleDropdown}>
          Actions ⌄
        </button>
        {dropdownOpen && (
          <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 min-w-[160px]">
            <div className="px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer">Already Completed</div>
            <div className="px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer">Upcoming Test</div>
            <div className="px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer">Reschedule Test</div>
          </div>
        )}
      </div>

      {loading ? (
        <p style={{ textAlign: 'center' }}>Loading tests...</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTests.map((test, idx) => (
            <div className="border border-slate-200 rounded-xl p-5 mb-4 hover:border-indigo-300 transition-colors" key={test.individual_test_id}>
              <div className="mb-3">
                <div className="font-semibold text-slate-800">{test.candidate_name}</div>
                {test.candidate_email && (
                  <p className="text-sm text-slate-500">{test.candidate_email}</p>
                )}
              </div>

              <div className="text-sm text-slate-700 space-y-1 mb-3">
                <p><strong>Topic:</strong> {test.topic}</p>
                <p><strong>Difficulty:</strong> {test.difficulty}</p>
                <p><strong>Test Type:</strong> {getTestTypeDisplay(test.test_type)}</p>
                <p><strong>Test Time:</strong> {test.scheduled_time}</p>
                <p><strong>Test id:</strong> {test.test_id}</p>
              </div>

              <div className="mb-3">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${test.is_completed ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                  {test.is_completed ? 'Completed' : 'Pending'}
                </span>
              </div>

              <div className="flex gap-2 flex-wrap">
                {!test.is_completed ? (
                  <>
                    <button
                      className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                      onClick={() => handleRescheduleTest(test.individual_test_id, test.candidate_name)}
                    >
                      Reschedule
                    </button>
                    <button
                      className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                      onClick={() => handleDeleteClick(test.test_id, test.candidate_email, test.candidate_name)}
                      disabled={deletingTests.has(`${test.test_id}_${test.candidate_email}`)}
                    >
                      {deletingTests.has(`${test.test_id}_${test.candidate_email}`) ? 'Deleting...' : 'Delete Test'}
                    </button>
                  </>
                ) : (
                  <>
                    <button className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors">View Result</button>
                    <button
                      className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                      onClick={() => handleDeleteClick(test.test_id, test.candidate_email, test.candidate_name)}
                      disabled={deletingTests.has(`${test.test_id}_${test.candidate_email}`)}
                    >
                      {deletingTests.has(`${test.test_id}_${test.candidate_email}`) ? 'Deleting...' : 'Delete Test'}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {!loading && filteredTests.length === 0 && (
        <p style={{ textAlign: 'center', marginTop: '20px' }}>
          No tests found for the selected job.
        </p>
      )}

      {/* Delete Confirmation Popup */}
      {showDeleteConfirm && deleteTarget && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Confirm Delete Test</h3>
            <div className="text-sm text-slate-700 space-y-1 mb-3">
              <p><strong>Candidate:</strong> {deleteTarget.candidateName}</p>
              <p><strong>Test ID:</strong> {deleteTarget.testId}</p>
              <p><strong>Email:</strong> {deleteTarget.candidateEmail}</p>
            </div>
            <p className="text-sm text-red-600 mb-5">
              Are you sure you want to delete this test? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
                onClick={handleDeleteCancel}
              >
                Cancel
              </button>
              <button
                className="flex-1 px-4 py-2.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                onClick={handleDeleteConfirm}
              >
                Delete Test
              </button>
            </div>
          </div>
        </div>
      )}
      {showReschedulePopup && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">Reschedule Test</h3>
              <button
                className="text-slate-400 hover:text-slate-600 text-xl font-bold cursor-pointer border-none bg-transparent"
                onClick={() => setShowReschedulePopup(false)}
              >
                &times;
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-slate-700 mb-4">
                <strong>Candidate:</strong> {rescheduleCandidateName}
              </p>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-slate-700">New Date & Time</label>
                <DateTimeSelector
                  value={rescheduleDate && rescheduleTime ? `${rescheduleDate}T${rescheduleTime}` : ''}
                  onChange={(datetime) => {
                    const [date, time] = datetime.split('T');
                    setRescheduleDate(date);
                    setRescheduleTime(time);
                  }}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                onClick={() => setShowReschedulePopup(false)}
                disabled={isRescheduling}
              >
                Cancel
              </button>
              <button
                className="flex-1 px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                onClick={handleRescheduleSubmit}
                disabled={isRescheduling || !rescheduleDate || !rescheduleTime}
              >
                {isRescheduling ? 'Rescheduling...' : 'Confirm Reschedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewTest;